import type { Market } from '../types';

export interface MarketScores {
  financial: number;
  market: number;
  appreciation: number;
  landlord: number;
  total: number;
}

/**
 * Calculate all category scores for a market
 */
export function calculateMarketScores(market: Market): MarketScores {
  // FINANCIAL SCORE (yield + price accessibility)
  const yieldPct = (market.gross_yield_annual || 0) * 100;
  const yieldScore = Math.min(100, Math.max(0, (yieldPct - 4) * (100 / 6)));
  const price = market.zhvi_current || 300000;
  const priceScore = Math.min(100, Math.max(0, (500000 - price) / 3500));
  const financial = Math.round((yieldScore * 0.7) + (priceScore * 0.3));

  // MARKET SCORE (days on market, price cuts, heat index)
  const dom = market.days_to_pending ?? 30;
  const domScore = Math.min(100, Math.max(0, (60 - dom) * (100 / 45)));
  const cuts = (market.price_cut_pct ?? 0) * 100;
  const cutsScore = Math.min(100, Math.max(0, (30 - cuts) * (100 / 25)));
  const heat = market.market_heat_index ?? 50;
  const marketScore = Math.round((domScore * 0.4) + (cutsScore * 0.3) + (heat * 0.3));

  // APPRECIATION SCORE (1yr, 3yr, 5yr trends)
  const appr1 = (market.appreciation_1yr ?? 0) * 100;
  const appr3 = (market.appreciation_3yr ?? 0) * 100;
  const appr5 = (market.appreciation_5yr ?? 0) * 100;
  const appr1Score = Math.min(100, Math.max(0, (appr1 + 5) * (100 / 15)));
  const appr3Score = Math.min(100, Math.max(0, (appr3 + 10) * (100 / 40)));
  const appr5Score = Math.min(100, Math.max(0, (appr5 + 10) * (100 / 60)));
  const appreciation = Math.round((appr1Score * 0.5) + (appr3Score * 0.3) + (appr5Score * 0.2));

  // LANDLORD SCORE (already 0-100)
  const landlord = market.landlord_score || 50;

  // TOTAL (weighted average)
  const total = Math.round(
    (financial * 0.35) +
    (marketScore * 0.20) +
    (appreciation * 0.20) +
    (landlord * 0.25)
  );

  return { financial, market: marketScore, appreciation, landlord, total };
}

/**
 * Get investment grade from score
 */
export function getGrade(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 75) return 'B+';
  if (score >= 65) return 'B';
  if (score >= 55) return 'C+';
  if (score >= 45) return 'C';
  return 'D';
}

/**
 * Get CSS class for grade
 */
export function getGradeClass(grade: string): string {
  return 'grade-' + grade.replace('+', 'plus');
}

/**
 * Get CSS class for yield value
 */
export function getYieldClass(yieldValue: number): string {
  const pct = yieldValue * 100;
  if (pct >= 8) return 'score-high';
  if (pct >= 6) return 'score-medium';
  return 'score-low';
}
